import { Flex } from 'antd';
import { TimeDetailWrapper } from '../../assets/styles/components/timesheetDetail.styled';
import { Paragraph, Spin } from '../Ant';
import { Calendar } from '../icons';
import { useLocation } from 'react-router-dom';
import { ITimesheetState } from '../../interface/timesheet';
import { useEffect, useState } from 'react';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useTranslation } from 'react-i18next';
import { useFetchDashboardTransactions } from '../../hooks/useFetchTransactions';
import { useUser } from '../../context/UserContext';
import { ROUTES, SystemConstants, UserType } from '../../constants/constants';
import { useInView } from 'react-intersection-observer';
import { ITransaction } from '../../interface/transaction';
import TransactionCard from '../dashboard/transactionCard';
import RenderTransactionRestrictions from '../dashboard/addTransaction/renderTransactionRestrictions';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useTransaction } from '../../context/TransactionContext';
import { useCopyTransaction } from '../../hooks/useCopyTransaction';
import SkeletonTransactionList from '../common/skeleton/skeletonTransactionList';
import { getLocalDateAndTime } from '../../constants/utilityFunctions';

const TimesheetTransactionList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useUser();
  const { setTitle } = usePage();
  const { goBack, push } = useNavigationStack();
  const { updateTransaction } = useTransaction();
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const [copyClicked, setCopyClicked] = useState<boolean>();
  const { copyTransaction, startTransactionOnSameMatter } = useCopyTransaction({
    setCopyClicked,
    setSelectedTransaction,
  });
  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true || user?.userType === UserType.RESTRICTED;
  const details = location?.state as ITimesheetState;
  const {
    data: transactionList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingTransactions,
    isFetching,
  } = useFetchDashboardTransactions({
    relations: [
      'matter:matterNumber,subject,currency,invoiceableType,language',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'unit:translations',
      'transactionType',
    ],
    pageSize: 5,
    extraFilters: {
      transactionUserId: user?.id ?? '',
      transactionDate: details?.transactionDate,
      matterId: `$in:${details?.matter?.id}`,
      unitId: SystemConstants.UNIT_HOUR,
      ...(details?.principalCompany && {
        principalCompanyId: `$eq:${details?.principalCompany?.id}`,
      }),
      ...(details?.principalPrivatePerson && {
        principalPrivatePersonId: `$eq:${details?.principalPrivatePerson?.id}`,
      }),
    },
    sortBy: ['transactionDate:DESC'],
    enabled: !!user?.id,
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'unitId',
      'unitPrice',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'matterId',
      'workedHours',
      'hasImportantNotes',
    ],
  });
  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && transactionList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const handleBack = () => {
    goBack();
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        subTitle={t('transaction:transactions')}
        title={details?.principalCompany?.name ?? details?.principalPrivatePerson?.fullName ?? ''}
        handleBack={handleBack}
      />,
    );
  }, []);
  const handleSelectTransaction = (transaction?: ITransaction) => {
    if (transaction && !copyClicked) {
      push(`${ROUTES.ADD_EDIT_TRANSACTION}/${transaction.id}`);
    } else if (transaction && copyClicked) {
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setCopyClicked(false);
      updateTransaction(null);
      setSelectedTransaction(undefined);
    }
  };
  const handleCopyTransaction = (transaction: ITransaction) => {
    copyTransaction(transaction);
  };
  const renderLoader = () => {
    if (isFetching) {
      return <Spin />;
    }
    return null;
  };
  return (
    <Flex vertical gap={10} className="h-full">
      <TimeDetailWrapper vertical gap={5}>
        <Paragraph $fontsize="13px" $weight={500} $lineheight="18px" $color="title">
          {details?.principalCompany?.name ?? details?.principalPrivatePerson?.fullName ?? ''}
        </Paragraph>
        <Paragraph $fontsize="13px" $lineheight="20px" $color="subtitle">
          {details?.matter?.matterNumber} - {details?.matter?.subject}
        </Paragraph>
        <Flex gap={4} align="center">
          <Calendar />
          <Paragraph $fontsize="14px" $lineheight="18px" $color="title">
            {getLocalDateAndTime(
              details?.transactionDate ? new Date(details.transactionDate) : undefined,
              true,
            )}
          </Paragraph>
        </Flex>
      </TimeDetailWrapper>
      <Flex vertical gap={10} className="overflow-y-auto scroll-stable-gutter">
        <>
          {transactionList.map((transaction) => (
            <TransactionCard
              data-testid="transaction-card"
              key={transaction.id}
              transaction={transaction}
              hidden={hidePricingInfo}
              onEdit={(transaction: ITransaction) => {
                setSelectedTransaction(transaction);
              }}
              onCopy={(transaction) => {
                handleCopyTransaction(transaction);
              }}
              onNewToSameMatter={startTransactionOnSameMatter}
            />
          ))}
          {hasNextPage && (
            <div ref={ref} data-testid="loading-indicator" className="loading-indicator" />
          )}
        </>

        {isLoadingTransactions ? <SkeletonTransactionList /> : renderLoader()}
      </Flex>
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleSelectTransaction}
      />
    </Flex>
  );
};

export default TimesheetTransactionList;
